/**
 * Orbit camera, Cities: Skylines conventions (spec §6.1).
 *
 * A perspective camera orbiting a ground focus point: altitude 15 m to 3 km, tilt
 * 20 deg to 85 deg, full 360 deg rotation, smooth damping. Key bindings become
 * configurable in M7; the defaults here are the ones spec §6.1 lists.
 */

import { Vector3 } from 'three';
import type { PerspectiveCamera } from 'three';

/** Camera limits from spec §6.1. */
export const CAMERA_LIMITS = {
  minAltitudeM: 15,
  maxAltitudeM: 3000,
  minTiltDeg: 20,
  maxTiltDeg: 85,
} as const;

/** Mutable orbit state, in metres, radians and metres per second. */
export interface OrbitState {
  /** Ground focus point. */
  readonly focus: Vector3;
  /** Distance from the focus to the camera (m). */
  distanceM: number;
  /** Azimuth around the focus (rad). */
  azimuthRad: number;
  /** Tilt above the horizon (rad). */
  tiltRad: number;
}

const DEG = Math.PI / 180;
/** Exponential damping rate; higher is snappier. */
const DAMPING_PER_S = 12;
/** Pan speed as a fraction of the current distance, per second. */
const PAN_FRACTION_PER_S = 0.6;
/** Rotation speed (rad/s) for Q and E. */
const ROTATE_RAD_PER_S = 1.4;
/** Below this the camera counts as arrived (m). Sub-pixel at any altitude. */
const DISTANCE_EPSILON_M = 1e-3;
/** Below this the camera counts as arrived (rad). */
const ANGLE_EPSILON_RAD = 1e-5;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Drives a `PerspectiveCamera` from keyboard and mouse input. */
export class OrbitController {
  readonly target: OrbitState;
  private readonly current: OrbitState;
  private readonly keys = new Set<string>();
  private disposers: Array<() => void> = [];

  constructor(
    private readonly camera: PerspectiveCamera,
    private readonly element: HTMLElement,
    focus = new Vector3(0, 0, 0),
  ) {
    const initial = {
      focus: focus.clone(),
      distanceM: 600,
      azimuthRad: 45 * DEG,
      tiltRad: 45 * DEG,
    };
    this.target = { ...initial, focus: focus.clone() };
    this.current = {
      ...initial,
      focus: focus.clone(),
      distanceM: initial.distanceM * 1.12,
      azimuthRad: initial.azimuthRad - 0.06,
    };
    this.attach();
    this.apply();
  }

  /** Current camera altitude above the focus plane (m), for the HUD readout. */
  get altitudeM(): number {
    return this.current.distanceM * Math.sin(this.current.tiltRad);
  }

  /** Current tilt above the horizon, in degrees. */
  get tiltDeg(): number {
    return this.current.tiltRad / DEG;
  }

  private attach(): void {
    const onKeyDown = (e: KeyboardEvent): void => {
      this.keys.add(e.key.toLowerCase());
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      this.keys.delete(e.key.toLowerCase());
    };
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      // Zoom towards the cursor is a multiplicative step, so each notch feels the
      // same at every altitude.
      const factor = Math.exp(e.deltaY * 0.0015);
      this.target.distanceM = clamp(
        this.target.distanceM * factor,
        CAMERA_LIMITS.minAltitudeM,
        CAMERA_LIMITS.maxAltitudeM / Math.sin(CAMERA_LIMITS.minTiltDeg * DEG),
      );
    };
    const onContextMenu = (e: Event): void => e.preventDefault();

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    this.element.addEventListener('wheel', onWheel, { passive: false });
    this.element.addEventListener('contextmenu', onContextMenu);
    this.disposers = [
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp),
      () => this.element.removeEventListener('wheel', onWheel),
      () => this.element.removeEventListener('contextmenu', onContextMenu),
    ];
  }

  /** Removes every listener this controller added. */
  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    this.keys.clear();
  }

  /**
   * Advances the camera by `dtS` seconds of real time.
   *
   * Returns whether anything actually moved. The host loop uses this to skip the
   * draw when the camera has settled and nothing else has changed, so a parked
   * camera costs nothing (and a software renderer is not pinned at 100%).
   */
  update(dtS: number): boolean {
    const dt = clamp(dtS, 0, 0.1);
    const panM = this.target.distanceM * PAN_FRACTION_PER_S * dt;

    // Pan in the camera's own horizontal frame, so W is always "away from you".
    const forwardX = Math.cos(this.target.azimuthRad);
    const forwardZ = Math.sin(this.target.azimuthRad);
    let dx = 0;
    let dz = 0;
    if (this.keys.has('w') || this.keys.has('arrowup')) {
      dx += forwardX;
      dz += forwardZ;
    }
    if (this.keys.has('s') || this.keys.has('arrowdown')) {
      dx -= forwardX;
      dz -= forwardZ;
    }
    if (this.keys.has('a') || this.keys.has('arrowleft')) {
      dx += forwardZ;
      dz -= forwardX;
    }
    if (this.keys.has('d') || this.keys.has('arrowright')) {
      dx -= forwardZ;
      dz += forwardX;
    }
    if (dx !== 0 || dz !== 0) {
      const len = Math.hypot(dx, dz);
      this.target.focus.x += (dx / len) * panM;
      this.target.focus.z += (dz / len) * panM;
    }

    if (this.keys.has('q')) this.target.azimuthRad -= ROTATE_RAD_PER_S * dt;
    if (this.keys.has('e')) this.target.azimuthRad += ROTATE_RAD_PER_S * dt;

    // Exponential damping towards the target: frame-rate independent.
    const k = 1 - Math.exp(-DAMPING_PER_S * dt);
    this.current.distanceM += (this.target.distanceM - this.current.distanceM) * k;
    this.current.azimuthRad += (this.target.azimuthRad - this.current.azimuthRad) * k;
    this.current.tiltRad += (this.target.tiltRad - this.current.tiltRad) * k;
    this.current.focus.lerp(this.target.focus, k);
    if (this.hasSettled()) {
      this.current.distanceM = this.target.distanceM;
      this.current.azimuthRad = this.target.azimuthRad;
      this.current.tiltRad = this.target.tiltRad;
      this.current.focus.copy(this.target.focus);
      this.apply();
      return false;
    }

    this.apply();
    return true;
  }

  /** Whether the damped camera has arrived at its target, to within a pixel. */
  private hasSettled(): boolean {
    return (
      Math.abs(this.target.distanceM - this.current.distanceM) < DISTANCE_EPSILON_M &&
      Math.abs(this.target.azimuthRad - this.current.azimuthRad) < ANGLE_EPSILON_RAD &&
      Math.abs(this.target.tiltRad - this.current.tiltRad) < ANGLE_EPSILON_RAD &&
      this.current.focus.distanceToSquared(this.target.focus) < DISTANCE_EPSILON_M ** 2
    );
  }

  private apply(): void {
    const tilt = clamp(
      this.current.tiltRad,
      CAMERA_LIMITS.minTiltDeg * DEG,
      CAMERA_LIMITS.maxTiltDeg * DEG,
    );
    const horizontal = this.current.distanceM * Math.cos(tilt);
    const vertical = this.current.distanceM * Math.sin(tilt);
    this.camera.position.set(
      this.current.focus.x + horizontal * Math.cos(this.current.azimuthRad),
      this.current.focus.y + vertical,
      this.current.focus.z + horizontal * Math.sin(this.current.azimuthRad),
    );
    this.camera.lookAt(this.current.focus);
  }
}
